/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { Pagination } from '@mui/material';
import usePageNavigation from '../../hooks/usePageNavigation';

import { getAllMyServiceReceipts } from '../../services/requests/GraphRequests';
import DatePickerRange from './DatePicker';

import { queryLimit } from '../../constants';
import { ROLE } from '../../constants';

export default function ServiceReceipts(props) {
    let navigate = useNavigate();
    const { t } = useTranslation();
    const { pageNumber = '1' } = useParams();
    const [sortingDates, setSortingDates] = useState({
        postedFrom: null,
        postedTo: null,
    });
    const [journal, setJournal] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCashRegister, setSelectedCashRegister] = useState('');
    const [selectedCashierId, setSelectedCashierId] = useState('');
    const [selectedCashierName, setSelectedCashierName] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(null);
    const [receiptsStart, setReceiptsStart] = useState(0);
    const { organization, role } = props;
    const redirectLink = 'client/service-receipts';

    usePageNavigation(
        setCurrentPage,
        setReceiptsStart,
        pageNumber,
        queryLimit,
        pageCount,
        navigate,
        redirectLink,
    );

    const handlePagination = (event, page) => {
        navigate(`/${redirectLink}/${page}`);
    };

    const getServiceReceipt = () => {
        getAllMyServiceReceipts(
            { ...sortingDates },
            queryLimit,
            receiptsStart,
            selectedCashRegister.id,
            selectedCashierId,
            selectedCashierName,
        ).then(data => {
            setJournal(data.data.getServiceReceipts.data);
            setPageCount(
                Math.ceil(data.data.getServiceReceipts.count / queryLimit),
            );
            setLoading(false);
        });
    };

    function handleChangeCash(event) {
        setSelectedCashRegister(event.target.value);
    }

    useEffect(() => {
        getServiceReceipt();
    }, [
        JSON.stringify(sortingDates),
        selectedCashRegister,
        selectedCashierId,
        selectedCashierName,
        currentPage,
    ]);

    if (loading) {
        return (
            <div align='center'>
                <CircularProgress />
            </div>
        );
    }

    if (!organization?.cashRegisters?.length) return null;
    return (
        <Box
            sx={{ flexGrow: 1, marginTop: 3, marginLeft: 10, marginBottom: 10 }}
        >
            <Grid container spacing={2} align='left'>
                <Grid item xs={5}>
                    <Typography
                        sx={{ fontSize: 24, marginTop: 3 }}
                        color='pimary'
                        gutterBottom
                    >
                        {t('service_checks')}:
                    </Typography>
                </Grid>
                {(role === ROLE.operator || role === ROLE.cashier) && (
                    <Grid item xs={6} sx={{ marginTop: 3 }} align='right'>
                        <Button
                            variant='outlined'
                            onClick={() => {
                                navigate('/client/new-service-receipt');
                            }}
                        >
                            {t('create_new_service_receipt')}
                        </Button>
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={2} align='left'>
                <Grid item xs={6}>
                    {organization?.cashRegisters.length > 0 && (
                        <TextField
                            id='standard-select-cash'
                            select
                            label={t('cash_machine')}
                            name='cash'
                            value={selectedCashRegister}
                            onChange={event => handleChangeCash(event)}
                            helperText={t('select_cash_register')}
                            fullWidth
                            sx={{ m: 1 }}
                            variant='standard'
                        >
                            {organization.cashRegisters.map(option => (
                                <MenuItem key={`${option.id}`} value={option}>
                                    {`${option.model} ${option.number}`}{' '}
                                    {option.id !== 2 ? '(fake)' : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Grid>
            </Grid>
            {!!selectedCashRegister && (
                <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={4} align='center'>
                        <DatePickerRange
                            setSortingDates={setSortingDates}
                            isCashSelected={!!selectedCashRegister}
                        />
                    </Grid>
                    <Grid item xs={3} align='center'>
                        <TextField
                            label={t('cashier_id')}
                            name='casheirId'
                            value={selectedCashierId}
                            onChange={event => {
                                setSelectedCashierName('');
                                setSelectedCashierId(event.target.value);
                            }}
                            helperText={t('type_cashier_id')}
                            fullWidth
                            sx={{ m: 1 }}
                            variant='standard'
                        />
                    </Grid>
                    <Grid item xs={3} align='center'>
                        <TextField
                            label={t('cashier_name_surname')}
                            name='casheirName'
                            value={selectedCashierName}
                            onChange={event => {
                                setSelectedCashierId('');
                                setSelectedCashierName(event.target.value);
                            }}
                            helperText={t('type_cashier_name')}
                            fullWidth
                            sx={{ m: 1 }}
                            variant='standard'
                        />
                    </Grid>
                </Grid>
            )}
            <Grid container spacing={2} align='left'>
                <Grid item xs={11}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('id')}</TableCell>
                                    <TableCell align='right'>
                                        {t('type')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('date_time')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('total')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('status')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('id_cashier')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('cashier_name')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {journal.map(receipt => {
                                    return (
                                        <TableRow
                                            hover
                                            key={receipt.soapData.id}
                                            sx={{
                                                '&:last-child td, &:last-child th':
                                                    { border: 0 },
                                            }}
                                            onClick={() => {
                                                navigate(
                                                    `/client/service-receipt/${receipt.id}`,
                                                );
                                            }}
                                        >
                                            <TableCell align='left'>
                                                {receipt.soapData.id}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {receipt.soapData.data.type}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {receipt.soapData.datetime}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {receipt.soapData.data.total}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {receipt.status}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {receipt.cashier &&
                                                receipt.cashier.id !== null
                                                    ? receipt.cashier.id
                                                    : ' '}
                                            </TableCell>
                                            <TableCell align='right'>
                                                {receipt.cashier &&
                                                receipt.cashier.name
                                                    ? receipt.cashier.name
                                                    : ' '}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
                {pageCount > 1 && (
                    <Grid item xs={11} align='center'>
                        <Pagination
                            sx={{
                                mt: 5,
                                justifyContent: 'center',
                                display: 'flex',
                            }}
                            count={pageCount}
                            variant='outlined'
                            shape='rounded'
                            page={+currentPage}
                            onChange={handlePagination}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}
